import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import "../css/auth.css";
import Navbar from "../components/navbar";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine starting mode based on URL parameters
  const urlToken = searchParams.get("token"); // For password resets
  const verifyToken = searchParams.get("verify"); // For email verification

  const [mode, setMode] = useState(() => {
    if (verifyToken) return "verify";
    if (urlToken) return "reset";
    return "signin";
  });

  // Form & Loading State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendStatus, setResendStatus] = useState("idle"); // idle, loading, success, error

  // Verification State (for when users click the email link)
  const [verificationStatus, setVerificationStatus] = useState("loading"); // loading, success, error

  // Custom Modal State (kept only for unexpected/general errors)
  const [modalConfig, setModalConfig] = useState({
    show: false,
    title: "",
    message: "",
    type: "error",
  });

  const API_URL =
    process.env.NODE_ENV === "production"
      ? "https://resumify.co.uk"
      : "http://localhost:5000";

  useEffect(() => {
    if (mode === "verify" && verifyToken) {
      const verifyUserEmail = async () => {
        try {
          const response = await fetch(`${API_URL}/api/verify-email`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ token: verifyToken }),
          });

          const data = await response.json();

          if (response.ok) {
            setVerificationStatus("success");
            if (data.user) {
              localStorage.setItem("user", JSON.stringify(data.user));
            }
          } else {
            setVerificationStatus("error");
          }
        } catch (err) {
          setVerificationStatus("error");
        }
      };

      verifyUserEmail();
    }
  }, [mode, verifyToken, API_URL]);

  // --- DYNAMIC SEO METADATA BASED ON MODE ---
  useEffect(() => {
    let pageTitle = "Resumify";
    let pageDesc = "Build your AI-optimized CV.";

    switch (mode) {
      case "signup":
        pageTitle = "Resumify | Sign Up";
        pageDesc = "Create a free account to build your AI-optimized CV.";
        break;
      case "signin":
        pageTitle = "Resumify | Sign In";
        break;
      case "forgot":
        pageTitle = "Resumify | Forgot Password";
        break;
      case "reset":
        pageTitle = "Resumify | Reset Password";
        break;
      case "verify":
        pageTitle = "Resumify | Verify Email";
        break;
      case "unverified":
        pageTitle = "Resumify | Check Your Email";
        break;
      default:
        break;
    }

    document.title = pageTitle;
  }, [mode]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const showModal = (title, message, type = "error") => {
    setModalConfig({ show: true, title, message, type });
  };

  const passwordReqs = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    special: /[^A-Za-z0-9]/.test(formData.password),
  };
  const isPasswordValid = Object.values(passwordReqs).every(Boolean);

  // --- RESEND VERIFICATION HANDLER (Inline UI) ---
  const handleResendVerification = async () => {
    setResendStatus("loading");
    try {
      const response = await fetch(`${API_URL}/api/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email }),
      });

      if (response.ok) {
        setResendStatus("success");
      } else {
        setResendStatus("error");
      }
    } catch (err) {
      setResendStatus("error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (mode === "signup" || mode === "reset") {
      if (!isPasswordValid) {
        showModal(
          "Weak Password",
          "Please ensure your password meets all requirements.",
          "error",
        );
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        showModal("Wait a second...", "Passwords do not match.", "error");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (mode === "signup" || mode === "signin") {
        const endpoint =
          mode === "signup" ? `${API_URL}/api/signup` : `${API_URL}/api/signin`;

        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          const needsVerification = data.message
            ?.toLowerCase()
            .includes("verify");
          if (needsVerification) {
            // Intercept unverified users and switch to the inline unverified UI instead of a modal
            setMode("unverified");
            setResendStatus("idle");
            return;
          }
          throw new Error(data.message);
        }

        if (mode === "signup") {
          // Immediately transition to unverified state after signup to prompt them to check email
          setMode("unverified");
          setResendStatus("idle");
        } else {
          localStorage.setItem("user", JSON.stringify(data.user));
          navigate("/builder");
        }
      } else if (mode === "forgot") {
        const response = await fetch(`${API_URL}/api/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: formData.email }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        showModal("Check your inbox", data.message, "success");
      } else if (mode === "reset") {
        const response = await fetch(`${API_URL}/api/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: urlToken,
            newPassword: formData.password,
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        showModal(
          "Success!",
          "Password reset successfully! Redirecting to login...",
          "success",
        );
        setTimeout(() => {
          setSearchParams({});
          setMode("signin");
          setModalConfig((prev) => ({ ...prev, show: false }));
          setFormData({ ...formData, password: "", confirmPassword: "" });
        }, 2500);
      }
    } catch (err) {
      showModal(
        "Authentication Error",
        err.message || "An error occurred.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeaders = () => {
    switch (mode) {
      case "signup":
        return {
          title: "Start getting interviews",
          sub: "Create an account to build your AI-optimized CV.",
        };
      case "forgot":
        return {
          title: "Reset Password",
          sub: "Enter your email and we'll send you a link.",
        };
      case "reset":
        return {
          title: "Set New Password",
          sub: "Please enter your new secure password.",
        };
      case "verify":
        return {
          title: "Verifying Account",
          sub: "We are confirming your email address securely.",
        };
      case "unverified":
        return {
          title: "Check your inbox",
          sub: "We need to verify your email address before you can log in.",
        };
      default:
        return {
          title: "Welcome back",
          sub: "Enter your details to access your CV dashboard.",
        };
    }
  };

  const { title, sub } = renderHeaders();

  return (
    <>
      <Navbar />
      <div className="auth-page">
        <div className="auth-form-section">
          <div className="auth-form-container">
            <div className="auth-header fade-content" key={mode}>
              <h1>{title}</h1>
              <p>{sub}</p>
            </div>

            {/* --- 1. VERIFICATION UI (From Email Link) --- */}
            {mode === "verify" ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                {verificationStatus === "loading" && (
                  <div className="fade-content">
                    <div
                      className="btn-spinner"
                      style={{
                        width: "40px",
                        height: "40px",
                        margin: "0 auto",
                        borderColor: "rgba(79, 70, 229, 0.3)",
                        borderTopColor: "#4f46e5",
                      }}
                    ></div>
                    <h3 style={{ marginTop: "20px", color: "#334155" }}>
                      Connecting to server...
                    </h3>
                  </div>
                )}

                {verificationStatus === "success" && (
                  <div className="fade-content">
                    <span style={{ fontSize: "3rem" }}>✅</span>
                    <h3
                      style={{
                        marginTop: "16px",
                        color: "#10b981",
                        marginBottom: "30px",
                      }}
                    >
                      Email Verified!
                    </h3>

                    <Link to="/builder" style={{ textDecoration: "none" }}>
                      <button
                        className="auth-submit"
                        style={{
                          cursor: "pointer",
                          background: "#4f46e5",
                          margin: 0,
                        }}
                      >
                        Go to Builder Now 🚀
                      </button>
                    </Link>
                  </div>
                )}

                {verificationStatus === "error" && (
                  <div className="fade-content">
                    <span style={{ fontSize: "3rem" }}>❌</span>
                    <h3 style={{ marginTop: "16px", color: "#f43f5e" }}>
                      Verification Failed
                    </h3>
                    <p
                      style={{
                        color: "#64748b",
                        marginTop: "10px",
                        marginBottom: "30px",
                      }}
                    >
                      This link is invalid or has expired.
                    </p>
                    <button
                      className="auth-submit"
                      style={{ margin: 0 }}
                      onClick={() => {
                        setSearchParams({});
                        setMode("signin");
                      }}
                    >
                      Return to Sign In
                    </button>
                  </div>
                )}

                {/* Locked State Shown when loading or errored */}
                {verificationStatus !== "success" && (
                  <div className="fade-content" style={{ marginTop: "20px" }}>
                    <button
                      className="auth-submit"
                      disabled
                      style={{
                        background: "#e2e8f0",
                        color: "#94a3b8",
                        cursor: "not-allowed",
                        margin: 0,
                        boxShadow: "none",
                      }}
                    >
                      🔒 Builder Locked (Awaiting Verification)
                    </button>
                  </div>
                )}
              </div>
            ) : mode === "unverified" ? (
              /* --- 2. UNVERIFIED STATE (Tried to login/signup without verifying) --- */
              <div
                className="fade-content"
                style={{ textAlign: "center", padding: "20px 0" }}
              >
                <span style={{ fontSize: "3rem" }}>✉️</span>
                <p
                  style={{
                    color: "#475569",
                    margin: "20px 0",
                    lineHeight: "1.5",
                  }}
                >
                  A verification link was sent to{" "}
                  <strong>{formData.email}</strong>.<br />
                  Please click the link in that email to unlock your account.
                </p>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    marginTop: "30px",
                  }}
                >
                  {resendStatus === "success" ? (
                    <div
                      style={{
                        padding: "12px",
                        background: "#dcfce7",
                        color: "#166534",
                        borderRadius: "8px",
                        fontWeight: "600",
                      }}
                    >
                      ✅ New link sent successfully!
                    </div>
                  ) : (
                    <button
                      onClick={handleResendVerification}
                      className="auth-submit"
                      disabled={resendStatus === "loading"}
                      style={{
                        margin: 0,
                        background: "#f8fafc",
                        color: "#334155",
                        border: "1px solid #cbd5e1",
                        boxShadow: "none",
                      }}
                    >
                      {resendStatus === "loading" ? (
                        <div
                          className="btn-spinner"
                          style={{
                            borderColor: "#cbd5e1",
                            borderTopColor: "#334155",
                          }}
                        ></div>
                      ) : (
                        "Resend Verification Email"
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => setMode("signin")}
                    className="auth-submit"
                    style={{ margin: 0 }}
                  >
                    Back to Sign In
                  </button>
                </div>
              </div>
            ) : (
              /* --- 3. STANDARD FORMS (Signin, Signup, Forgot, Reset) --- */
              <form className="auth-form" onSubmit={handleSubmit}>
                <div
                  className={`expandable-field ${mode === "signup" ? "expanded" : ""}`}
                >
                  <div className="input-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Alex Carter"
                      required={mode === "signup"}
                    />
                  </div>
                </div>

                <div
                  className={`expandable-field ${mode === "signup" || mode === "signin" || mode === "forgot" ? "expanded" : ""}`}
                >
                  <div className="input-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                      required={mode !== "reset"}
                    />
                  </div>
                </div>

                <div
                  className={`expandable-field ${mode === "signup" || mode === "signin" || mode === "reset" ? "expanded" : ""}`}
                >
                  <div className="input-group">
                    <div className="label-row">
                      <label htmlFor="password">
                        {mode === "reset" ? "New Password" : "Password"}
                      </label>
                      {mode === "signin" && (
                        <span
                          className="forgot-link"
                          style={{ cursor: "pointer" }}
                          onClick={() => setMode("forgot")}
                        >
                          Forgot password?
                        </span>
                      )}
                    </div>
                    <input
                      type="password"
                      id="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      required={mode !== "forgot"}
                    />

                    <div
                      className={`expandable-field ${mode === "signup" || mode === "reset" ? "expanded" : ""}`}
                    >
                      <div
                        className="password-requirements"
                        style={{ marginTop: "12px" }}
                      >
                        <p>Password must contain:</p>
                        <ul>
                          <li className={passwordReqs.length ? "valid" : ""}>
                            <span className="icon">
                              {passwordReqs.length ? "✓" : "○"}
                            </span>{" "}
                            At least 8 characters
                          </li>
                          <li className={passwordReqs.uppercase ? "valid" : ""}>
                            <span className="icon">
                              {passwordReqs.uppercase ? "✓" : "○"}
                            </span>{" "}
                            One uppercase letter
                          </li>
                          <li className={passwordReqs.number ? "valid" : ""}>
                            <span className="icon">
                              {passwordReqs.number ? "✓" : "○"}
                            </span>{" "}
                            One number
                          </li>
                          <li className={passwordReqs.special ? "valid" : ""}>
                            <span className="icon">
                              {passwordReqs.special ? "✓" : "○"}
                            </span>{" "}
                            One special character
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`expandable-field ${mode === "signup" || mode === "reset" ? "expanded" : ""}`}
                >
                  <div className="input-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      required={mode === "signup" || mode === "reset"}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-submit fade-content"
                  key={mode + "-btn"}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="btn-spinner"></div>
                  ) : (
                    <>
                      {mode === "signup" && "Create Account"}
                      {mode === "signin" && "Sign In"}
                      {mode === "forgot" && "Send Reset Link"}
                      {mode === "reset" && "Update Password"}
                    </>
                  )}
                </button>
              </form>
            )}

            {/* --- FOOTER TOGGLES --- */}
            {mode !== "verify" && mode !== "unverified" && (
              <div className="auth-footer fade-content" key={mode + "-footer"}>
                <p>
                  {mode === "signup"
                    ? "Already have an account?"
                    : mode === "signin"
                      ? "Don't have an account?"
                      : "Remembered your password?"}{" "}
                  <button
                    className="toggle-btn"
                    onClick={() => {
                      setMode(
                        mode === "signup"
                          ? "signin"
                          : mode === "signin"
                            ? "signup"
                            : "signin",
                      );
                      setFormData({
                        ...formData,
                        password: "",
                        confirmPassword: "",
                      });
                    }}
                    type="button"
                  >
                    {mode === "signup" || mode === "forgot" || mode === "reset"
                      ? "Sign in"
                      : "Sign up for free"}
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* --- VISUAL SIDE PANEL --- */}
        <div className="auth-visual-section">
          <div className="visual-content">
            <div className="testimonial-badge">⭐ 92% ATS Pass Rate</div>
            <div className="glass-testimonial">
              <p>
                "I was stuck in the application black hole for months. I used
                this AI to rewrite my CV on a Tuesday, and by Friday I had three
                interview requests from top tech companies. It actually works."
              </p>
              <div className="glass-author">
                <div className="author-avatar"></div>
                <div className="author-info">
                  <strong>David K.</strong>
                  <span>Hired at Stripe</span>
                </div>
              </div>
            </div>
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
          </div>
        </div>
      </div>

      {/* --- REUSABLE POPUP MODAL (For standard errors only) --- */}
      {modalConfig.show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            animation: "fadeIn 0.2s ease",
          }}
          onClick={() => setModalConfig({ ...modalConfig, show: false })}
        >
          <div
            style={{
              background: "white",
              padding: "32px",
              borderRadius: "20px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              animation: "modalPop 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: "3.5rem",
                margin: "0 auto 16px auto",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {modalConfig.type === "error" ? "⚠️" : "✅"}
            </div>
            <h2
              style={{
                marginBottom: "12px",
                color: "#0f172a",
                fontSize: "1.5rem",
                fontWeight: "800",
              }}
            >
              {modalConfig.title}
            </h2>
            <p
              style={{
                color: "#475569",
                marginBottom: "28px",
                lineHeight: "1.5",
                fontSize: "0.95rem",
              }}
            >
              {modalConfig.message}
            </p>
            <button
              onClick={() => setModalConfig({ ...modalConfig, show: false })}
              style={{
                width: "100%",
                padding: "14px",
                background:
                  modalConfig.type === "error" ? "#f43f5e" : "#10b981",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "1rem",
                transition: "transform 0.1s ease",
              }}
              onMouseOver={(e) => (e.target.style.transform = "scale(0.98)")}
              onMouseOut={(e) => (e.target.style.transform = "scale(1)")}
            >
              Okay, Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
